import { type ReactNode, useLayoutEffect } from 'react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

/**
 * Shared shell for the public patient portal (/appointments/*).
 *
 * The portal mirrors the design system of the clinic website (josenika.cz):
 * Poppins 600 headings on #242424, Hind body text, flat white panels with
 * #dedede borders and square corners on a #f5f8f9 page, and the site's gold
 * #d7ad6c for labels, accents and buttons (uppercase, letter-spaced).
 * The class fragments below exist so every portal page composes the same
 * look — they are portal-only and must not leak into the staff app.
 */

export const portalHeading =
	'font-[Poppins,sans-serif] font-semibold text-[#242424]'

/** Small gold section label rendered above a heading (site `.h6.text-color`). */
export const portalLabel =
	'font-[Poppins,sans-serif] text-base font-semibold text-[#d7ad6c]'

/** Solid gold CTA (site `.btn-main`): square, uppercase, white text. */
export const portalButton =
	'rounded-none bg-[#d7ad6c] text-[13px] font-normal uppercase tracking-[1px] text-white hover:bg-[#cc9744]'

/** Gold-bordered secondary button (site `.btn-solid-border`). */
export const portalButtonOutline =
	'rounded-none border-2 border-[#d7ad6c] bg-transparent text-[13px] font-normal uppercase tracking-[1px] text-[#242424] hover:bg-[#d7ad6c] hover:text-white'

/** Flat white panel (site sections/cards): square, thin neutral border. */
export const portalPanel = 'border border-[#dedede] bg-white'

/** Form input matching the site: square, neutral border, gold focus border. */
export const portalInput =
	'rounded-none border-[#dedede] bg-white text-[#242424] focus-visible:border-[#d7ad6c]'

/** Heading with the site's 55×2px gold underline bar (site `h3.quote`). */
export const portalHeadingBar =
	'relative pb-3 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-[55px] after:bg-[#d7ad6c]'

const FONTS_ID = 'portal-josenika-fonts'
const FONTS_HREF =
	'https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600&family=Poppins:wght@500;600&display=swap'

export function PortalShell({ children }: { children: ReactNode }) {
	useLayoutEffect(() => {
		// Load the site's fonts (Poppins + Hind) on first portal render. The
		// link stays after unmount: staff routes don't reference the families,
		// and removing it would flash fallback fonts on the next portal visit.
		if (!document.getElementById(FONTS_ID)) {
			const link = document.createElement('link')
			link.id = FONTS_ID
			link.rel = 'stylesheet'
			link.href = FONTS_HREF
			document.head.appendChild(link)
		}

		// The portal is always light: patients never see the staff theme. The
		// staff theme lives in the `data-theme` attribute on <html> (consumed
		// by daisyui), which defaults to "dark" — without this override a
		// patient gets dark native form controls (color-scheme: dark) on top
		// of the portal's light design. The previous value is restored on
		// unmount so staff navigating back keep their chosen theme.
		const root = document.documentElement
		const previousTheme = root.getAttribute('data-theme')
		root.setAttribute('data-theme', 'light')
		return () => {
			if (previousTheme) {
				root.setAttribute('data-theme', previousTheme)
			} else {
				root.removeAttribute('data-theme')
			}
		}
	}, [])

	return (
		<div className="min-h-screen bg-[#f5f8f9] font-[Hind,sans-serif] text-[17px] leading-normal text-black/65 [color-scheme:light]">
			{children}
		</div>
	)
}

/** Full-screen centered content (loading, error and login states). */
export function PortalCentered({ children }: { children: ReactNode }) {
	return (
		<PortalShell>
			<div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
				{children}
			</div>
		</PortalShell>
	)
}

export function PortalLoading() {
	return (
		<PortalCentered>
			<LoadingSpinner size="lg" />
		</PortalCentered>
	)
}
